import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { TicketIcon, TrashSimple, FloppyDisk } from "@phosphor-icons/react";

// Convert ISO date to `YYYY-MM-DDTHH:mm` (value expected by <input type="datetime-local">)
const toLocalInputValue = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

// Convert `datetime-local` value back to ISO with local offset
const fromLocalInputValue = (v) => {
  if (!v) return "";
  return new Date(v).toISOString();
};

const formatDateShort = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(lang === "de" ? "de-CH" : lang === "fr" ? "fr-CH" : "it-CH", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function PromoManager({ onChange }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const [state, setState] = useState({ promo: null, default_commission_rate: 0.1, is_active: false });
  const [form, setForm] = useState({ label: "", rate_pct: "5", ends_at: "", active: true });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/promo");
      setState(data);
      if (data.promo) {
        setForm({
          label: data.promo.label || "",
          rate_pct: String(Math.round((data.promo.rate ?? 0) * 100 * 100) / 100),
          ends_at: toLocalInputValue(data.promo.ends_at),
          active: !!data.promo.active,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label.trim()) return toast.error(t("promo.error_label"));
    const rate = parseFloat(form.rate_pct);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) return toast.error(t("promo.error_rate"));
    if (!form.ends_at) return toast.error(t("promo.error_ends"));
    setSaving(true);
    try {
      await api.put("/admin/promo", {
        label: form.label.trim(),
        rate: rate / 100,
        ends_at: fromLocalInputValue(form.ends_at),
        active: form.active,
      });
      toast.success(t("promo.saved"));
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(t("promo.confirm_clear"))) return;
    setSaving(true);
    try {
      await api.delete("/admin/promo");
      toast.success(t("promo.cleared"));
      setForm({ label: "", rate_pct: "5", ends_at: "", active: true });
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = useMemo(() => {
    if (!state.promo) {
      return { label: t("promo.status_none"), color: "text-zinc-400 border-white/20" };
    }
    if (state.is_active) return { label: t("promo.status_active"), color: "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/5" };
    return { label: t("promo.status_inactive"), color: "text-zinc-400 border-white/20" };
  }, [state, t]);

  if (loading) return <div className="text-zinc-400 p-6">…</div>;

  return (
    <div className="space-y-6" data-testid="promo-manager">
      <div className="border border-white/10 bg-[#0A0A0A] p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <TicketIcon size={22} className="text-[#E30613]" weight="duotone" />
              <h3 className="font-display text-xl uppercase font-black">{t("promo.section_title")}</h3>
              <span className={`inline-flex items-center px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border ${statusBadge.color}`} data-testid="promo-status">
                {statusBadge.label}
              </span>
            </div>
            <p className="text-xs text-zinc-400 mt-2 max-w-2xl leading-relaxed">{t("promo.section_hint", { default_rate: Math.round(state.default_commission_rate * 100) })}</p>
          </div>
          {state.promo && (
            <Button
              type="button"
              variant="outline"
              onClick={clear}
              disabled={saving}
              className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-xs font-bold rounded-none h-10"
              data-testid="promo-clear"
            >
              <TrashSimple size={14} className="mr-1" weight="bold" /> {t("promo.clear")}
            </Button>
          )}
        </div>

        {state.promo && state.is_active && (
          <div className="border-l-2 border-[#00FF66] pl-4 mb-6 text-sm">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00FF66] font-bold">{t("promo.currently_active")}</div>
            <div className="mt-1 text-white font-bold">
              {state.promo.label} — {Math.round(state.promo.rate * 100 * 100) / 100}% {t("promo.commission")}
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {t("promo.until")} {formatDateShort(state.promo.ends_at, lang)}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="promo-label" className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{t("promo.field_label")}</Label>
            <Input
              id="promo-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t("promo.field_label_placeholder")}
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="promo-label"
            />
          </div>
          <div>
            <Label htmlFor="promo-rate" className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{t("promo.field_rate")}</Label>
            <Input
              id="promo-rate"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={form.rate_pct}
              onChange={(e) => setForm({ ...form, rate_pct: e.target.value })}
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="promo-rate"
            />
            <div className="text-[10px] text-zinc-500 mt-1">{t("promo.field_rate_hint", { default_rate: Math.round(state.default_commission_rate * 100) })}</div>
          </div>
          <div>
            <Label htmlFor="promo-ends" className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{t("promo.field_ends")}</Label>
            <Input
              id="promo-ends"
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="promo-ends"
            />
          </div>
          <div className="flex items-end gap-3">
            <div className="flex items-center gap-3 border border-white/10 bg-black px-4 h-10 mt-6">
              <Switch id="promo-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active" />
              <Label htmlFor="promo-active" className="text-xs uppercase tracking-widest font-bold text-white cursor-pointer">
                {t("promo.field_active")}
              </Label>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-11 px-8"
            data-testid="promo-save"
          >
            <FloppyDisk size={14} className="mr-2" weight="fill" /> {saving ? t("promo.saving") : t("promo.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
